// ink-component
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';

import DropdownAura from '../DropdownAura/DropdownAura';
import styles from './styles';
import useWindowWidth from '../../../../hooks/useWindowWidth';

export interface DropdownItemProps {
  /** Dropdown.Item's interactive part - usually a Dropdown.Button, Dropdown.Anchor, or any other component - as a render prop */
  render: (props?: any) => JSX.Element;
  /** Whether the hover style should display or not */
  hover?: boolean;
  /** The item is marked as active */
  active?: boolean;
  /** The item is disabled */
  disabled?: boolean;
  /** Whether the item is sticked on top or not */
  sticky?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  role?: any;
  /** @ignore */
  'aria-selected'?: any;
}

/** DropdownItemElement replaces the previous cloning method for rendering children */
const DropdownItemElement = styled.div`
  ${styles}
`;

/**
 * @purpose Wraps each option inside a Dropdown
 * @categories navigation
 * @group Dropdown
 */
const DropdownItem = (props: DropdownItemProps) => {
  const { sticky, active, hover, role, 'aria-selected': ariaSelected, render, ...otherProps } = props;
  const { props: renderProps, type } = render();
  const { isMobile } = useWindowWidth();

  const itemProps = {
    ...otherProps,
    ...renderProps,
    role: 'button',
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        active,
        hover,
        disabled: props.disabled,
        sticky,
        type: type.displayName,
        mobile: isMobile,
      })}
    >
      <DropdownAura role={role} aria-selected={ariaSelected}>
        <DropdownItemElement {...itemProps} as={type} />
      </DropdownAura>
    </ThemeProvider>
  );
};

DropdownItem.defaultProps = {
  hover: true,
  active: false,
  disabled: false,
  sticky: false,
};

DropdownItem.displayName = 'Dropdown.Item';

export default DropdownItem;
